import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../../../services/useAuth";
import { useReportFilters } from "../shared/useReportFilters";
import { pad, toISO } from "../shared/utils";
import { API_URL } from "../shared/constants";

export function usePartProductionReport() {
  const { getToken } = useAuth();
  const filters = useReportFilters();

  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [partData, setPartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState("day");
  const [toasts, setToasts] = useState([]);

  const authH = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );
  const addToast = useCallback(
    (msg, type = "success") =>
      setToasts((p) => [...p, { id: Date.now(), message: msg, type }]),
    [],
  );
  const dismissToast = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        const [mR, sR] = await Promise.all([
          axios.get(`${API_URL}/api/machines`, authH()),
          axios.get(`${API_URL}/api/shifts`, authH()),
        ]);
        setMachines(mR.data.machines || mR.data || []);
        setShifts(sR.data.shifts || []);
      } catch {}
    })();
  }, [authH]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set("from", toISO(filters.startDate, false));
      p.set("to", toISO(filters.endDate, true));
      if (filters.machineIds.length)
        p.set("machineId", filters.machineIds.join(","));
      p.set("groupBy", groupBy);
      const r = await axios.get(
        `${API_URL}/api/reports/production-by-part?${p}`,
        authH(),
      );
      setPartData(r.data.data);
    } catch {
      addToast("Parça üretim verisi yüklenemedi", "danger");
      setPartData(null);
    } finally {
      setLoading(false);
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.machineIds,
    groupBy,
    authH,
    addToast,
  ]);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line
  useEffect(() => {
    fetchData();
  }, [groupBy]); // eslint-disable-line

  const filtersKey = `${filters.startDate}|${filters.endDate}|${filters.machineIds.join(",")}`;
  useEffect(() => {
    fetchData();
  }, [filtersKey]); // eslint-disable-line

  const derived = useMemo(() => {
    if (!partData) return null;
    const { parts = [], timeline = [], machineBreakdown = [] } = partData;

    const dateKeys = [
      ...new Set(
        timeline.map((t) => {
          const d = t.date;
          return d.day != null
            ? `${d.year}-${pad(d.month || 1)}-${pad(d.day)}`
            : d.week != null
              ? `${d.year}-W${pad(d.week)}`
              : `${d.year}-${pad(d.month || 1)}`;
        }),
      ),
    ].sort();

    const topParts = parts.slice(0, 8);
    const timelineSeries = topParts.map((p) => ({
      name: p.partName || p.partCode,
      data: dateKeys.map((dk) => {
        const entry = timeline.find((t) => {
          const d = t.date;
          const key =
            d.day != null
              ? `${d.year}-${pad(d.month || 1)}-${pad(d.day)}`
              : d.week != null
                ? `${d.year}-W${pad(d.week)}`
                : `${d.year}-${pad(d.month || 1)}`;
          return key === dk && String(t.partId) === String(p.partId);
        });
        return entry?.qty || 0;
      }),
    }));

    return { parts, topParts, dateKeys, timelineSeries, machineBreakdown };
  }, [partData]);

  return {
    filters,
    machines,
    shifts,
    loading,
    derived,
    groupBy,
    setGroupBy,
    fetchData,
    toasts,
    dismissToast,
  };
}
